'use client'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  getVisualActivityFor,
  getVisualActivitySnapshot,
  installVisualRegistryDocumentController,
  noteStartupScopeScan,
  reconcileVisualScopeAnimationTarget,
  registerDatasetScope,
  registerNearestDatasetScope,
  subscribeVisualScopeLifecycle,
  teardownVisualActivityRegistry,
} from '../../lib/visual-runtime/visualActivityRegistry'
import { getAnimatedAssetSnapshot } from '../../lib/visual-runtime/animatedAssetRegistry'
import { isMobileVideoMotionPauseRuntime, isVideoHeavyActivityActive, subscribeVideoHeavyActivity } from '../../lib/videoPipelineProgress'

let controllerGeneration = 0
let pendingDestructiveTeardown = 0

const ENTER_SURFACE_SELECTOR = ['card', 'row', 'ad', 'panel']
  .map((kind) => `[data-ql7-visual-scope="${kind}"]`).join(',')
const enteredSurfaces = new WeakSet()

// One-shot presentation only. Keep it separate from the motion governor: CSS
// transitions keep running during iOS momentum scrolling, unlike keyframes.
export function installViewportEnterTransitions() {
  if (typeof IntersectionObserver !== 'function') return () => {}
  const preference = window.matchMedia?.('(prefers-reduced-motion: reduce), (forced-colors: active)')
  if (preference?.matches || navigator.connection?.saveData) return () => {}
  const pending = new Map()
  const entering = new Set()
  const queued = new Set()
  let firstFrame = 0
  let secondFrame = 0
  let disposed = false
  const release = (node) => {
    observer.unobserve(node)
    pending.delete(node)
    entering.delete(node)
    queued.delete(node)
    if (['pending', 'shown'].includes(node.getAttribute('data-ql7-enter'))) node.removeAttribute('data-ql7-enter')
  }
  const show = (node, prepared = false) => {
    if (enteredSurfaces.has(node)) return
    enteredSurfaces.add(node)
    observer.unobserve(node)
    pending.delete(node)
    if (prepared) {
      // Offscreen cards already painted their starting state. Reveal in this
      // observer delivery, without adding two frames to a moving viewport.
      entering.add(node)
      node.setAttribute('data-ql7-enter', 'shown')
      return
    }
    queued.add(node)
    // A shared two-frame commit gives new DOM one painted starting state. No
    // forced layout, per-card timers, frame-by-frame JS or scroll listeners.
    if (firstFrame || secondFrame) return
    firstFrame = window.requestAnimationFrame(() => {
      firstFrame = 0
      secondFrame = window.requestAnimationFrame(() => {
        secondFrame = 0
        for (const target of queued) {
          if (!target.isConnected) { release(target); continue }
          entering.add(target)
          target.setAttribute('data-ql7-enter', 'shown')
        }
        queued.clear()
      })
    })
  }
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const node = entry.target
      if (!pending.has(node)) continue
      if (!node.isConnected) { release(node); continue }
      const state = pending.get(node)
      const rect = entry.boundingClientRect
      const alreadyVisible = !rect || (rect.bottom > 0 && rect.top < window.innerHeight)
      if (state.initial && entry.isIntersecting && alreadyVisible) {
        // SSR/previously painted content must never flash visible -> hidden.
        enteredSurfaces.add(node)
        release(node)
      } else {
        node.setAttribute('data-ql7-enter', 'pending')
        if (entry.isIntersecting) show(node, state.prepared)
        else pending.set(node, { initial: false, prepared: true })
      }
    }
  }, { root: null, rootMargin: '0px', threshold: [0, 0.001] })
  const unsubscribe = subscribeVisualScopeLifecycle(({ node, registered, initial }) => {
    if (!registered) { release(node); return }
    if (disposed || preference?.matches || enteredSurfaces.has(node) || pending.has(node)) return
    if (!node.matches(ENTER_SURFACE_SELECTOR) || node.closest('[data-ql7-enter="off"], [role="dialog"], [role="alert"], [role="status"]')) return
    // Reveal the complete card (avatar/name/media/body/counters) as one unit.
    // Large parent panels/rails yield to their actual nested card owners.
    if (node.querySelector(ENTER_SURFACE_SELECTOR)) return
    pending.set(node, { initial, prepared: false })
    if (!initial) node.setAttribute('data-ql7-enter', 'pending')
    try { observer.observe(node) } catch { release(node) }
  })
  const onTransitionEnd = (event) => {
    if (event.propertyName === 'opacity' && entering.has(event.target)) release(event.target)
  }
  const clear = () => {
    if (firstFrame) window.cancelAnimationFrame(firstFrame)
    if (secondFrame) window.cancelAnimationFrame(secondFrame)
    firstFrame = secondFrame = 0
    for (const node of new Set([...pending.keys(), ...entering, ...queued])) release(node)
    observer.disconnect()
  }
  const onPreference = (event) => { if (event.matches) clear() }
  document.addEventListener('transitionend', onTransitionEnd, true)
  document.addEventListener('transitioncancel', onTransitionEnd, true)
  preference?.addEventListener?.('change', onPreference)
  return () => {
    disposed = true
    unsubscribe()
    clear()
    document.removeEventListener('transitionend', onTransitionEnd, true)
    document.removeEventListener('transitioncancel', onTransitionEnd, true)
    preference?.removeEventListener?.('change', onPreference)
  }
}

const IOS_SCROLL_RASTER_GUARD_REVISION = 'W16_SCROLL_OWNER_PAUSE_DISABLED_2026_10_29'
const IOS_SCROLL_RASTER_GUARD_ATTR = 'data-ql7-ios-scroll-raster-guard'
const IOS_SCROLL_MOTION_STOP_ATTR = 'data-ql7-scroll-motion-stopped'
const IOS_SCROLL_RASTER_GUARD_EVENT = 'ql7:ios-scroll-raster-guard'
const IOS_SCROLL_GUARD_SCROLL_HOLD_MS = 1400
const IOS_SCROLL_GUARD_MAINTENANCE_MS = 240

export function isIOSScrollRasterGuardRuntime(
  navigatorLike = typeof navigator !== 'undefined' ? navigator : null,
) {
  const ua = String(navigatorLike?.userAgent || '')
  const platform = String(navigatorLike?.platform || '')
  const maxTouchPoints = Number(navigatorLike?.maxTouchPoints || 0)
  return /iPhone|iPad|iPod/i.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1)
}

export function isMobileScrollAnimationStopRuntime(
  navigatorLike = typeof navigator !== 'undefined' ? navigator : null,
  windowLike = typeof window !== 'undefined' ? window : null,
) {
  if (isIOSScrollRasterGuardRuntime(navigatorLike)) return true
  if (navigatorLike?.userAgentData?.mobile === true) return true
  if (/Android|Mobile|Windows Phone/i.test(String(navigatorLike?.userAgent || ''))) return true
  const hasTouch = Number(navigatorLike?.maxTouchPoints || 0) > 0
  let coarse = false
  try { coarse = !!windowLike?.matchMedia?.('(pointer: coarse)')?.matches } catch {}
  return hasTouch && coarse
}

function cancelPendingDestructiveTeardown() {
  if (!pendingDestructiveTeardown || typeof window === 'undefined') return
  window.clearTimeout(pendingDestructiveTeardown)
  pendingDestructiveTeardown = 0
}

function scheduleDestructiveTeardown(generation) {
  if (typeof window === 'undefined') return
  cancelPendingDestructiveTeardown()
  pendingDestructiveTeardown = window.setTimeout(() => {
    pendingDestructiveTeardown = 0
    if (controllerGeneration !== generation) return
    teardownVisualActivityRegistry()
  }, 0)
}


// A single global hint, not an animation/observer per avatar. The existing
// clickable-avatar data attribute covers Battle Chat, forum, DM, search,
// Quantum Family, recommendation cards and MetaMarket user rows.
const AVATAR_TAP_SELECTOR = '[data-user-info-clickable="1"], [data-avatar-hint-target="1"]'
const AVATAR_TAP_FIRST_DELAY_MS = 30_000
const AVATAR_TAP_INTERVAL_MS = 180_000
const AVATAR_TAP_DURATION_MS = 2800
const AVATAR_TAP_SCROLL_QUIET_MS = 180

function readVisibleAvatarRect(element) {
  if (!element?.isConnected) return null
  if (element.getAttribute('data-user-info-clickable') !== '1' && element.getAttribute('data-avatar-hint-target') !== '1') return null
  const rect = element.getBoundingClientRect()
  if (rect.width < 12 || rect.height < 12) return null

  let left = Math.max(0, rect.left)
  let top = Math.max(0, rect.top)
  let right = Math.min(window.innerWidth, rect.right)
  let bottom = Math.min(window.innerHeight, rect.bottom)
  if (right <= left || bottom <= top) return null

  // An avatar inside a scrolling list/popover must be actually visible, not
  // merely within the browser viewport behind the list's clipping boundary.
  for (let parent = element; parent && parent.nodeType === 1; parent = parent.parentElement) {
    if (parent.hidden || parent.hasAttribute('inert') || (parent !== element && parent.getAttribute('aria-hidden') === 'true')) return null
    const style = window.getComputedStyle(parent)
    if (style.display === 'none' || style.visibility !== 'visible' || Number(style.opacity) < 0.05) return null
    if (parent === element || parent === document.body || parent === document.documentElement) continue
    if (/(hidden|auto|scroll|clip)/.test(`${style.overflowX} ${style.overflowY}`)) {
      const clip = parent.getBoundingClientRect()
      left = Math.max(left, clip.left)
      top = Math.max(top, clip.top)
      right = Math.min(right, clip.right)
      bottom = Math.min(bottom, clip.bottom)
      if (right <= left || bottom <= top) return null
    }
  }
  if ((right - left) * (bottom - top) < rect.width * rect.height * 0.60) return null

  // Reject avatars covered by another panel, dialog, sticky header, etc.
  const hit = document.elementFromPoint((left + right) / 2, (top + bottom) / 2)
  if (!hit || !(element.contains(hit) || hit.contains(element))) return null

  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
    radius: window.getComputedStyle(element).borderRadius,
  }
}

function AvatarClickHintRuntime() {
  const [hint, setHint] = useState(null)
  const lastAvatarRef = useRef(null)
  const cycleRef = useRef(0)
  const hideTimerRef = useRef(0)
  const scrollQuietTimerRef = useRef(0)
  const scrollActiveRef = useRef(false)
  const hintVisibleRef = useRef(false)

  useEffect(() => {
    const tick = () => {
      if (scrollActiveRef.current) return
      if (isVideoHeavyActivityActive() && isMobileVideoMotionPauseRuntime()) return
      if (document.visibilityState === 'hidden') return
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return
      if (navigator.connection?.saveData) return

      const visible = []
      for (const element of document.querySelectorAll(AVATAR_TAP_SELECTOR)) {
        const rect = readVisibleAvatarRect(element)
        if (rect) visible.push({ element, rect })
      }
      if (!visible.length) return
      const pool = visible.length > 1
        ? visible.filter(({ element }) => element !== lastAvatarRef.current)
        : visible
      const picked = pool[Math.floor(Math.random() * pool.length)]
      lastAvatarRef.current = picked.element
      hintVisibleRef.current = true
      setHint({ target: picked.element, rect: picked.rect, cycle: ++cycleRef.current })
      window.clearTimeout(hideTimerRef.current)
      hideTimerRef.current = window.setTimeout(() => {
        hintVisibleRef.current = false
        setHint(null)
      }, AVATAR_TAP_DURATION_MS)
    }
    let interval = 0
    const firstTimer = window.setTimeout(() => {
      tick()
      interval = window.setInterval(tick, AVATAR_TAP_INTERVAL_MS)
    }, AVATAR_TAP_FIRST_DELAY_MS)
    return () => {
      window.clearTimeout(firstTimer)
      window.clearInterval(interval)
      window.clearTimeout(hideTimerRef.current)
      hintVisibleRef.current = false
    }
  }, [])

  useEffect(() => {
    const stopForScroll = () => {
      scrollActiveRef.current = true
      window.clearTimeout(scrollQuietTimerRef.current)
      window.clearTimeout(hideTimerRef.current)
      if (hintVisibleRef.current) {
        hintVisibleRef.current = false
        setHint(null)
      }
      scrollQuietTimerRef.current = window.setTimeout(() => {
        scrollActiveRef.current = false
        scrollQuietTimerRef.current = 0
      }, AVATAR_TAP_SCROLL_QUIET_MS)
    }
    document.addEventListener('scroll', stopForScroll, { capture: true, passive: true })
    window.addEventListener('wheel', stopForScroll, { passive: true })
    window.addEventListener('touchmove', stopForScroll, { passive: true })
    window.visualViewport?.addEventListener('scroll', stopForScroll, { passive: true })
    return () => {
      document.removeEventListener('scroll', stopForScroll, true)
      window.removeEventListener('wheel', stopForScroll)
      window.removeEventListener('touchmove', stopForScroll)
      window.visualViewport?.removeEventListener('scroll', stopForScroll)
      window.clearTimeout(scrollQuietTimerRef.current)
      scrollActiveRef.current = false
      scrollQuietTimerRef.current = 0
    }
  }, [])

  // A scheduled hint must not animate (or schedule a geometry rAF) during
  // conversion. The normal 30s/180s cadence remains unchanged after resume.
  useEffect(() => subscribeVideoHeavyActivity((active) => {
    if (!active || !isMobileVideoMotionPauseRuntime()) return
    window.clearTimeout(hideTimerRef.current)
    hintVisibleRef.current = false
    setHint(null)
  }), [])

  const activeAvatar = hint?.target || null
  const activeCycle = hint?.cycle || 0

  useEffect(() => {
    if (!activeAvatar) return undefined
    let pendingFrame = 0
    const update = () => {
      pendingFrame = 0
      const rect = readVisibleAvatarRect(activeAvatar)
      if (!rect) {
        setHint((current) => current?.cycle === activeCycle ? null : current)
        return
      }
      setHint((current) => {
        if (current?.cycle !== activeCycle) return current
        const old = current.rect
        if (old.left === rect.left && old.top === rect.top && old.width === rect.width && old.height === rect.height && old.radius === rect.radius) return current
        return { ...current, rect }
      })
    }
    const schedule = () => {
      if (!pendingFrame) pendingFrame = window.requestAnimationFrame(update)
    }
    window.addEventListener('resize', schedule, { passive: true })
    window.visualViewport?.addEventListener('resize', schedule, { passive: true })
    let observer = null
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(schedule)
      observer.observe(activeAvatar)
    }
    return () => {
      window.removeEventListener('resize', schedule)
      window.visualViewport?.removeEventListener('resize', schedule)
      if (pendingFrame) window.cancelAnimationFrame(pendingFrame)
      observer?.disconnect()
    }
  }, [activeAvatar, activeCycle])

  if (!hint || typeof document === 'undefined') return null
  const { rect } = hint
  return createPortal(
    <span
      key={hint.cycle}
      className="ql7AvatarTapHint"
      data-avatar-tap-hint="1"
      data-ql7-ios-scroll-raster-exempt="1"
      aria-hidden="true"
      style={{
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        borderRadius: rect.radius,
        '--ql7-tap-size': `${Math.min(rect.width, rect.height)}px`,
      }}
    >
      <span className="ql7AvatarTapDim" />
      <span className="ql7AvatarTapRipple ql7AvatarTapRipple--one" />
      <span className="ql7AvatarTapRipple ql7AvatarTapRipple--two" />
      <span className="ql7AvatarTapRipple ql7AvatarTapRipple--three" />
      <svg className="ql7AvatarTapHand" viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <path
          d="M18 25V9a3 3 0 0 1 6 0v14.5l2.5-2.2a3.3 3.3 0 0 1 4.7.5l.7.8a3.5 3.5 0 0 1 4.2 1.1l.5.8a3.4 3.4 0 0 1 4.4 3.3l-.1 5c-.1 5.7-4.2 9.7-9.8 10.2h-7.8a10 10 0 0 1-7.8-3.8l-6.6-8.4a3.3 3.3 0 0 1 5.2-4.1l3.9 4.7V25Z"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="rgba(10,25,43,.20)"
        />
        <path d="M24 23.5v5m7.1-6v6m5.5-4v5" stroke="white" strokeWidth="2" strokeLinecap="round" opacity=".83" />
      </svg>
    </span>,
    document.body,
  )
}

export default function GlobalVisualActivityRuntime() {
  useEffect(() => {
    const generation = ++controllerGeneration
    cancelPendingDestructiveTeardown()
    const uninstallDocument = installVisualRegistryDocumentController()

    // Registration is safe during the root passive effect because generic
    // dataset scopes no longer publish React-unknown visual-state attributes.
    // Product/browser state remains owned by its product components; the visual
    // runtime owns only animation lifecycle.
    const startupScopes = [...document.querySelectorAll('[data-ql7-visual-scope]')]
    noteStartupScopeScan(startupScopes.length)
    for (const scope of startupScopes) registerDatasetScope(scope)
    const uninstallEnterTransitions = installViewportEnterTransitions()

    // Physical iPhone evidence showed that the scroll-only pause governor was
    // itself a compositor-pressure source: one captured second produced 128
    // marked owners and 40 attribute mutations while the page rendered at
    // 20.8fps. Keep the detector/snapshot for forensic comparability, but never
    // arm the scroll governor. Near/offscreen animation ownership is unchanged
    // and remains controlled by the visual registry.
    const mobileScrollRuntime = isMobileScrollAnimationStopRuntime()
    const iosScrollGuardEnabled = false
    const iosScrollGuardRoot = document.documentElement
    let iosScrollGuardActive = false
    let iosScrollGuardDeadline = 0
    let iosScrollGuardTimer = 0
    const iosScrollGuardCounters = {
      activations: 0,
      releases: 0,
      scrollEvents: 0,
      scrollEndEvents: 0,
      languageAnchorBypasses: 0,
      seededAnimations: 0,
      targetsRemembered: 0,
      targetStops: 0,
      targetRestarts: 0,
      staleTargetsPruned: 0,
      maintenanceRuns: 0,
      maintenancePruned: 0,
    }
    const canUseWeakRef = typeof WeakRef === 'function'
    const scrollMotionTargetRefs = new Set()
    const scrollMotionRefByTarget = new WeakMap()
    const scrollMotionFallbackTargets = canUseWeakRef ? null : new Set()
    const scrollMotionMarkedTargets = new Set()

    const isScrollMotionTarget = (target) => {
      if (!(target instanceof Element) || !target.isConnected) return false
      if (!target.closest?.('[data-ql7-visual-scope]')) return false
      return !target.closest?.('[data-ql7-ios-scroll-raster-exempt="1"]')
    }

    const markScrollMotionTarget = (target) => {
      if (!iosScrollGuardActive || !isScrollMotionTarget(target)) return
      if (target.getAttribute(IOS_SCROLL_MOTION_STOP_ATTR) === '1') return
      target.setAttribute(IOS_SCROLL_MOTION_STOP_ATTR, '1')
      const marker = canUseWeakRef ? scrollMotionRefByTarget.get(target) : target
      if (marker) scrollMotionMarkedTargets.add(marker)
      iosScrollGuardCounters.targetStops += 1
    }

    const rememberScrollMotionTarget = (target) => {
      if (!iosScrollGuardEnabled) return
      if (!isScrollMotionTarget(target) || scrollMotionRefByTarget.has(target)) {
        if (iosScrollGuardActive) markScrollMotionTarget(target)
        return
      }
      if (canUseWeakRef) {
        const ref = new WeakRef(target)
        scrollMotionRefByTarget.set(target, ref)
        scrollMotionTargetRefs.add(ref)
      } else {
        scrollMotionRefByTarget.set(target, target)
        scrollMotionFallbackTargets.add(target)
      }
      iosScrollGuardCounters.targetsRemembered += 1
      if (iosScrollGuardActive) markScrollMotionTarget(target)
    }

    const untrackScrollMotionTarget = (target) => {
      const ref = scrollMotionRefByTarget.get(target)
      if (!ref) return
      scrollMotionRefByTarget.delete(target)
      if (canUseWeakRef) {
        scrollMotionTargetRefs.delete(ref)
        scrollMotionMarkedTargets.delete(ref)
      } else {
        scrollMotionFallbackTargets.delete(target)
        scrollMotionMarkedTargets.delete(target)
      }
    }

    const forgetScrollMotionTarget = (target) => {
      if (!(target instanceof Element)) return
      // A detached owner must never be retained until a potentially absent
      // scrollend after long iOS momentum/reversal sequences.
      const marker = canUseWeakRef ? scrollMotionRefByTarget.get(target) : target
      if (marker && scrollMotionMarkedTargets.has(marker)) {
        if (target.isConnected) return
        target.removeAttribute?.(IOS_SCROLL_MOTION_STOP_ATTR)
        untrackScrollMotionTarget(target)
        iosScrollGuardCounters.staleTargetsPruned += 1
        return
      }
      untrackScrollMotionTarget(target)
    }

    const pruneDisconnectedScrollMotionTargets = () => {
      iosScrollGuardCounters.maintenanceRuns += 1
      let pruned = 0
      for (const marker of [...scrollMotionMarkedTargets]) {
        const target = canUseWeakRef ? marker.deref?.() : marker
        if (target instanceof Element && target.isConnected) continue
        target?.removeAttribute?.(IOS_SCROLL_MOTION_STOP_ATTR)
        scrollMotionMarkedTargets.delete(marker)
        if (target instanceof Element) untrackScrollMotionTarget(target)
        else if (canUseWeakRef) scrollMotionTargetRefs.delete(marker)
        iosScrollGuardCounters.staleTargetsPruned += 1
        pruned += 1
      }
      iosScrollGuardCounters.maintenancePruned += pruned
      return pruned
    }

    const forEachScrollMotionTarget = (callback) => {
      if (!canUseWeakRef) {
        for (const target of [...scrollMotionFallbackTargets]) {
          if (!isScrollMotionTarget(target)) {
            scrollMotionFallbackTargets.delete(target)
            scrollMotionRefByTarget.delete(target)
            iosScrollGuardCounters.staleTargetsPruned += 1
            continue
          }
          callback(target)
        }
        return
      }
      for (const ref of [...scrollMotionTargetRefs]) {
        const target = ref.deref()
        if (!isScrollMotionTarget(target)) {
          scrollMotionTargetRefs.delete(ref)
          if (target instanceof Element) scrollMotionRefByTarget.delete(target)
          iosScrollGuardCounters.staleTargetsPruned += 1
          continue
        }
        callback(target)
      }
    }

    // Dynamic surfaces register lazily when an animation actually starts inside
    // their nearest explicit scope. The same event records only the concrete
    // CSSAnimation owner for the mobile scroll guard.
    const onAnimationStart = (event) => {
      const target = event?.target
      if (!(target instanceof Element)) return
      const scope = registerNearestDatasetScope(target, { fromAnimationStart: true })
      if (scope) reconcileVisualScopeAnimationTarget(scope, target)
      rememberScrollMotionTarget(target)
    }
    const onAnimationFinished = (event) => forgetScrollMotionTarget(event?.target)
    document.addEventListener('animationstart', onAnimationStart, true)
    if (iosScrollGuardEnabled) {
      document.addEventListener('animationend', onAnimationFinished, true)
      document.addEventListener('animationcancel', onAnimationFinished, true)
    }

    // Hydrated/SSR animations may already be running before the passive effect.
    // Seed once from the browser animation index; this is not repeated on scroll.
    try {
      const existingAnimations = document.getAnimations?.() || []
      for (const animation of existingAnimations) {
        if (typeof animation?.animationName !== 'string') continue
        const target = animation?.effect?.target
        if (!(target instanceof Element)) continue
        iosScrollGuardCounters.seededAnimations += 1
        const scope = registerNearestDatasetScope(target)
        if (scope) reconcileVisualScopeAnimationTarget(scope, target)
        rememberScrollMotionTarget(target)
      }
    } catch {}

    const emitIOSScrollGuard = (active, reason) => {
      try {
        window.dispatchEvent(new CustomEvent(IOS_SCROLL_RASTER_GUARD_EVENT, {
          detail: {
            revision: IOS_SCROLL_RASTER_GUARD_REVISION,
            active: !!active,
            reason: String(reason || ''),
            ts: Date.now(),
          },
        }))
      } catch {}
    }

    const releaseIOSScrollGuard = (reason = 'quiet') => {
      if (!iosScrollGuardActive) return
      iosScrollGuardActive = false
      iosScrollGuardCounters.releases += 1
      for (const marker of [...scrollMotionMarkedTargets]) {
        const target = canUseWeakRef ? marker.deref?.() : marker
        if (!(target instanceof Element)) {
          scrollMotionMarkedTargets.delete(marker)
          if (canUseWeakRef) scrollMotionTargetRefs.delete(marker)
          iosScrollGuardCounters.staleTargetsPruned += 1
          continue
        }
        if (!target.isConnected) {
          target.removeAttribute?.(IOS_SCROLL_MOTION_STOP_ATTR)
          untrackScrollMotionTarget(target)
          iosScrollGuardCounters.staleTargetsPruned += 1
          continue
        }
        target.removeAttribute?.(IOS_SCROLL_MOTION_STOP_ATTR)
        iosScrollGuardCounters.targetRestarts += 1
      }
      scrollMotionMarkedTargets.clear()
      iosScrollGuardRoot?.removeAttribute?.(IOS_SCROLL_RASTER_GUARD_ATTR)
      emitIOSScrollGuard(false, reason)
    }

    const scheduleIOSScrollGuardRelease = () => {
      if (!iosScrollGuardEnabled || iosScrollGuardTimer) return
      const remaining = iosScrollGuardDeadline - Date.now()
      const delay = Math.max(80, Math.min(IOS_SCROLL_GUARD_MAINTENANCE_MS, remaining))
      iosScrollGuardTimer = window.setTimeout(() => {
        iosScrollGuardTimer = 0
        pruneDisconnectedScrollMotionTargets()
        if (Date.now() < iosScrollGuardDeadline) {
          scheduleIOSScrollGuardRelease()
          return
        }
        releaseIOSScrollGuard('quiet')
      }, delay)
    }

    const activateIOSScrollGuard = (reason, holdMs) => {
      if (!iosScrollGuardEnabled) return
      iosScrollGuardDeadline = Math.max(
        iosScrollGuardDeadline,
        Date.now() + Math.max(250, Number(holdMs) || IOS_SCROLL_GUARD_SCROLL_HOLD_MS),
      )
      if (!iosScrollGuardActive) {
        iosScrollGuardActive = true
        iosScrollGuardCounters.activations += 1
        iosScrollGuardRoot?.setAttribute?.(IOS_SCROLL_RASTER_GUARD_ATTR, '1')
        forEachScrollMotionTarget(markScrollMotionTarget)
        emitIOSScrollGuard(true, reason)
      }
      scheduleIOSScrollGuardRelease()
    }

    const onIOSScroll = () => {
      if (!iosScrollGuardEnabled) return
      try {
        const programmaticTs = Number(window.__forumProgrammaticScrollTs || 0)
        const programmaticReason = String(window.__forumProgrammaticScrollReason || '')
        if (programmaticReason === 'language_switch_anchor' && Date.now() - programmaticTs < 260) {
          iosScrollGuardCounters.languageAnchorBypasses += 1
          return
        }
      } catch {}
      iosScrollGuardCounters.scrollEvents += 1
      activateIOSScrollGuard('scroll', IOS_SCROLL_GUARD_SCROLL_HOLD_MS)
    }
    const onIOSScrollEnd = () => {
      if (!iosScrollGuardEnabled) return
      iosScrollGuardCounters.scrollEndEvents += 1
      if (iosScrollGuardTimer) window.clearTimeout(iosScrollGuardTimer)
      iosScrollGuardTimer = 0
      iosScrollGuardDeadline = 0
      releaseIOSScrollGuard('scrollend')
    }

    if (iosScrollGuardEnabled) {
      document.addEventListener('scroll', onIOSScroll, true)
      document.addEventListener('scrollend', onIOSScrollEnd, true)
    }

    window.__ql7IosScrollRasterGuardSnapshot = () => ({
      ...(() => {
        let live = 0
        let connected = 0
        for (const marker of scrollMotionMarkedTargets) {
          const target = canUseWeakRef ? marker.deref?.() : marker
          if (!(target instanceof Element)) continue
          live += 1
          if (target.isConnected) connected += 1
        }
        return {
          activeTargetMarkers: live,
          activeConnectedTargetMarkers: connected,
          activeDisconnectedTargetMarkers: live - connected,
          activeTargetMarkerRefs: scrollMotionMarkedTargets.size,
          strongMarkedTargetCount: canUseWeakRef ? 0 : scrollMotionMarkedTargets.size,
        }
      })(),
      revision: IOS_SCROLL_RASTER_GUARD_REVISION,
      enabled: iosScrollGuardEnabled,
      mobileRuntime: mobileScrollRuntime,
      scope: 'mobile',
      active: iosScrollGuardActive,
      deadline: iosScrollGuardDeadline,
      trackedTargetRefs: canUseWeakRef ? scrollMotionTargetRefs.size : scrollMotionFallbackTargets.size,
      ...iosScrollGuardCounters,
    })

    window.__ql7VisualActivitySnapshot = getVisualActivitySnapshot
    window.__ql7VisualActivityFor = getVisualActivityFor
    window.__ql7AnimatedAssetSnapshot = getAnimatedAssetSnapshot

    return () => {
      uninstallEnterTransitions()
      document.removeEventListener('animationstart', onAnimationStart, true)
      if (iosScrollGuardEnabled) {
        document.removeEventListener('animationend', onAnimationFinished, true)
        document.removeEventListener('animationcancel', onAnimationFinished, true)
      }
      if (iosScrollGuardEnabled) {
        document.removeEventListener('scroll', onIOSScroll, true)
        document.removeEventListener('scrollend', onIOSScrollEnd, true)
      }
      if (iosScrollGuardTimer) window.clearTimeout(iosScrollGuardTimer)
      iosScrollGuardTimer = 0
      iosScrollGuardDeadline = 0
      releaseIOSScrollGuard('unmount')
      for (const marker of scrollMotionMarkedTargets) {
        const target = canUseWeakRef ? marker.deref?.() : marker
        target?.removeAttribute?.(IOS_SCROLL_MOTION_STOP_ATTR)
      }
      scrollMotionMarkedTargets.clear()
      scrollMotionTargetRefs.clear()
      scrollMotionFallbackTargets?.clear?.()
      iosScrollGuardRoot?.removeAttribute?.(IOS_SCROLL_RASTER_GUARD_ATTR)
      uninstallDocument?.()
      try { delete window.__ql7VisualActivitySnapshot } catch {}
      try { delete window.__ql7VisualActivityFor } catch {}
      try { delete window.__ql7AnimatedAssetSnapshot } catch {}
      try { delete window.__ql7IosScrollRasterGuardSnapshot } catch {}

      // React StrictMode replays passive effects in development. Destructive
      // teardown must wait one task so the immediate replay can cancel it;
      // otherwise component-owned visual subscribers are silently erased while
      // their mounted DOM nodes remain alive.
      scheduleDestructiveTeardown(generation)
    }
  }, [])

  return <AvatarClickHintRuntime />
}
